import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { Profile, ProfilesApiResponse, WorksheetLoadResponse, WorksheetMode, WorksheetSheet } from '../../models/worksheet-app.model';
import { WorksheetAppApiService } from '../../services/worksheet-app-api.service';
import {
	ClearWorksheetAppState,
	DiscardWorksheetDraft,
	InitWorksheetAppState,
	LoadWorksheet,
	LoadWorksheetProfiles,
	PersistWorksheetDraft,
	RefreshWorksheet,
	SaveWorksheet,
	SetWorksheetDraft,
	SetWorksheetPolling,
} from '../actions/worksheet-app.actions';

/**
 * Worksheet Apps — NGXS state slice owning the read-only worksheet lifecycle
 * (Phase 1). Caches the active profile's sheets (computed values + cell
 * structure), the profile list, and a thin UI scratchpad (loading / error /
 * lastRefresh / polling).
 *
 * <p>Named {@code worksheetApp} to stay distinct from any unrelated slice. The
 * raw {@code cellStructure} is preserved per sheet untouched (a later phase's
 * save path echoes it back verbatim — SSOT with the live legacy renderer).
 * See documentation/implementation/system/tms-worksheet-phase1.md.
 */

export class WorksheetAppStateModel {
	appName!: string | null;
	/** Server-resolved localized app title (language-aware); null until first load. */
	title!: string | null;
	profileCd!: string | null;
	sheets!: ReadonlyArray<WorksheetSheet>;
	activeSheet!: string | null;
	profiles!: ReadonlyArray<Profile>;
	/** profileCd of an UNSAVED imported draft (null = none). Drives the "modified, not saved" dot. */
	draftProfileCd!: string | null;
	/** build = editable formula text; execute = computed, read-only. */
	mode!: WorksheetMode;
	loading!: boolean;
	/** Save in flight (distinct from the full-grid `loading` spinner). */
	saving!: boolean;
	error!: string | null;
	/** Epoch ms of the last successful load/refresh (client clock — matches legacy "Last Refresh"). */
	lastRefresh!: number | null;
	polling!: boolean;
	/** Entitled mode codes from the load response (legacy entitledModesStr); 'build' gates the Build toggle. */
	entitledModes!: ReadonlyArray<string>;
	/** Admin flag from the load response — gates the Flush Cubes button. */
	isAdmin!: boolean;
	/**
	 * True ONLY after a build-mode load has applied its sheets — i.e. the grid
	 * currently holds editable formula TEXT, not execute-mode computed values.
	 * `mode` flips to 'build' synchronously at the start of a load, but the build
	 * data arrives async; saving in that window would persist the still-showing
	 * computed values OVER the formulas (the corruption that hit "Current day by
	 * category"). The save guard requires this flag, so a save can only ever
	 * serialize a genuine build-mode grid. Reset to false at load start, on
	 * execute load, and on clear/init.
	 */
	buildGridReady!: boolean;
}

const DEFAULT_WORKSHEET_APP_STATE: WorksheetAppStateModel = {
	appName: null,
	title: null,
	profileCd: null,
	sheets: [],
	draftProfileCd: null,
	activeSheet: null,
	profiles: [],
	mode: 'execute',
	loading: false,
	saving: false,
	error: null,
	lastRefresh: null,
	polling: false,
	entitledModes: [],
	isAdmin: false,
	buildGridReady: false,
};

@State<WorksheetAppStateModel>({
	name: 'worksheetApp',
	defaults: DEFAULT_WORKSHEET_APP_STATE,
})
@Injectable()
export class WorksheetAppState {
	@Selector() static appName(state: WorksheetAppStateModel): string | null {
		return state.appName;
	}
	@Selector() static title(state: WorksheetAppStateModel): string | null {
		return state.title;
	}
	@Selector() static profileCd(state: WorksheetAppStateModel): string | null {
		return state.profileCd;
	}
	@Selector() static sheets(state: WorksheetAppStateModel): ReadonlyArray<WorksheetSheet> {
		return state.sheets;
	}
	@Selector() static activeSheet(state: WorksheetAppStateModel): string | null {
		return state.activeSheet;
	}
	@Selector() static profiles(state: WorksheetAppStateModel): ReadonlyArray<Profile> {
		return state.profiles;
	}
	@Selector() static draftProfileCd(state: WorksheetAppStateModel): string | null {
		return state.draftProfileCd;
	}
	/** True when the active profile is the unsaved imported draft (drives the dot + Update button). */
	@Selector() static isProfileModified(state: WorksheetAppStateModel): boolean {
		return state.draftProfileCd !== null && state.profileCd === state.draftProfileCd;
	}
	/** Server profiles + the unsaved draft (under "Custom") so it shows in the dropdown. */
	@Selector() static profilesWithDraft(state: WorksheetAppStateModel): ReadonlyArray<Profile> {
		if (!state.draftProfileCd || state.profiles.some((p: Profile): boolean => p.profileCd === state.draftProfileCd)) {
			return state.profiles;
		}
		return [...state.profiles, { profileCd: state.draftProfileCd, group: 'Custom' }];
	}
	@Selector() static mode(state: WorksheetAppStateModel): WorksheetMode {
		return state.mode;
	}
	@Selector() static loading(state: WorksheetAppStateModel): boolean {
		return state.loading;
	}
	@Selector() static saving(state: WorksheetAppStateModel): boolean {
		return state.saving;
	}
	@Selector() static error(state: WorksheetAppStateModel): string | null {
		return state.error;
	}
	@Selector() static lastRefresh(state: WorksheetAppStateModel): number | null {
		return state.lastRefresh;
	}
	@Selector() static polling(state: WorksheetAppStateModel): boolean {
		return state.polling;
	}
	@Selector() static entitledModes(state: WorksheetAppStateModel): ReadonlyArray<string> {
		return state.entitledModes;
	}

	@Selector() static isAdmin(state: WorksheetAppStateModel): boolean {
		return state.isAdmin;
	}

	@Selector() static buildGridReady(state: WorksheetAppStateModel): boolean {
		return state.buildGridReady;
	}

	constructor(private readonly api: WorksheetAppApiService) {}

	@Action(InitWorksheetAppState)
	initState(ctx: StateContext<WorksheetAppStateModel>, action: InitWorksheetAppState): void {
		// Fresh slice for a new app open — drop any prior app's sheets/profiles.
		ctx.setState({
			...DEFAULT_WORKSHEET_APP_STATE,
			appName: action.appName,
			profileCd: action.profileCd ?? null,
		});
	}

	@Action(ClearWorksheetAppState)
	clearState(ctx: StateContext<WorksheetAppStateModel>): void {
		ctx.setState({ ...DEFAULT_WORKSHEET_APP_STATE });
	}

	@Action(LoadWorksheetProfiles)
	async loadProfiles(ctx: StateContext<WorksheetAppStateModel>, action: LoadWorksheetProfiles): Promise<void> {
		try {
			const resp: ProfilesApiResponse = await firstValueFrom(this.api.getProfiles(action.appName));
			ctx.patchState({ profiles: resp.profiles ?? [] });
		} catch (err: unknown) {
			// Profiles are non-fatal — the worksheet still loads its default profile.
			console.warn('[WorksheetAppState] LoadWorksheetProfiles failed', err);
			ctx.patchState({ profiles: [] });
		}
	}

	@Action(LoadWorksheet)
	async loadWorksheet(ctx: StateContext<WorksheetAppStateModel>, action: LoadWorksheet): Promise<void> {
		// `mode` flips now, but the grid still shows the PRIOR mode's data until
		// this load resolves — clear buildGridReady so a save in that window is
		// blocked (it would persist the old computed values over the formulas).
		// Loading a real (server) profile discards any unsaved import draft.
		ctx.patchState({ loading: true, error: null, profileCd: action.profileCd, mode: action.mode, buildGridReady: false, draftProfileCd: null });
		try {
			const resp: WorksheetLoadResponse = await firstValueFrom(this.api.load(action.appName, action.profileCd, action.mode));
			const sheets: ReadonlyArray<WorksheetSheet> = WorksheetAppState.normalizeSheets(resp);
			ctx.patchState({
				sheets,
				activeSheet: sheets.length > 0 ? sheets[0].name : null,
				// Fall back to the appCd when the worksheet carries no title, so a
				// title-less app still clears the breadcrumb's labelPending gate
				// (mirrors TSM's appName fallback) instead of hiding the chip.
				title: resp.title || action.appName,
				entitledModes: resp.entitledModes ?? [],
				isAdmin: resp.isAdmin ?? false,
				loading: false,
				lastRefresh: Date.now(),
				// Now the grid holds genuine build-mode formula text — safe to save.
				buildGridReady: action.mode === 'build',
			});
		} catch (err: unknown) {
			ctx.patchState({ loading: false, error: WorksheetAppState.errorMessage(err) });
		}
	}

	@Action(SaveWorksheet)
	async saveWorksheet(ctx: StateContext<WorksheetAppStateModel>, action: SaveWorksheet): Promise<void> {
		ctx.patchState({ saving: true, error: null });
		try {
			await firstValueFrom(this.api.save(action.appName, action.profileCd, action.sheets));
			ctx.patchState({ saving: false });
		} catch (err: unknown) {
			// Surface the error and rethrow so the page's Start flow does NOT
			// proceed to execute mode on a failed save (atomicity — It-4).
			ctx.patchState({ saving: false, error: WorksheetAppState.errorMessage(err) });
			throw err;
		}
	}

	@Action(SetWorksheetDraft)
	setWorksheetDraft(ctx: StateContext<WorksheetAppStateModel>, action: SetWorksheetDraft): void {
		// Render the imported sheets directly (no HTTP load) as an unsaved draft in
		// BUILD mode (formula text). buildGridReady=true so the grid can be serialized
		// for the Update (save-as-new) persist.
		ctx.patchState({
			sheets: action.sheets,
			activeSheet: action.sheets.length > 0 ? action.sheets[0].name : null,
			profileCd: action.profileCd,
			draftProfileCd: action.profileCd,
			mode: 'build',
			loading: false,
			error: null,
			buildGridReady: true,
			lastRefresh: Date.now(),
		});
	}

	@Action(PersistWorksheetDraft)
	async persistWorksheetDraft(ctx: StateContext<WorksheetAppStateModel>, action: PersistWorksheetDraft): Promise<void> {
		ctx.patchState({ saving: true });
		try {
			await firstValueFrom(this.api.saveAsNew(action.appName, action.profileCd, action.sheets));
			// Saved: it's a real profile now — clear the draft, select the saved name,
			// refresh the list.
			ctx.patchState({ saving: false, draftProfileCd: null, profileCd: action.profileCd });
			await ctx.dispatch(new LoadWorksheetProfiles(action.appName)).toPromise();
		} catch (err: unknown) {
			// CRITICAL: do NOT set the grid-blanking `error` state — a save-as-new
			// failure (e.g. duplicate name) must leave the worksheet + draft fully
			// intact so the user can rename and retry. Just rethrow for the page's
			// inline rename-retry flow (industry-standard save-as conflict handling).
			ctx.patchState({ saving: false });
			throw err;
		}
	}

	@Action(DiscardWorksheetDraft)
	discardWorksheetDraft(ctx: StateContext<WorksheetAppStateModel>): void {
		ctx.patchState({ draftProfileCd: null });
	}

	@Action(RefreshWorksheet)
	async refreshWorksheet(ctx: StateContext<WorksheetAppStateModel>, action: RefreshWorksheet): Promise<void> {
		const { appName, profileCd, mode }: WorksheetAppStateModel = ctx.getState();
		// Never refresh in build mode — a refresh returns execute-mode COMPUTED
		// values, which would clobber the formula text being edited (and a
		// subsequent Save would persist values over formulas). Mirrors the legacy
		// refreshMonitor guard (WebSpreadsheetMonitor.refreshMonitor). (Review M1.)
		if (!appName || !profileCd || mode === 'build') {
			return;
		}
		try {
			// Silent refresh — no loading spinner; just re-write the computed values.
			const resp: WorksheetLoadResponse = await firstValueFrom(this.api.refresh(appName, profileCd));
			ctx.patchState({
				sheets: WorksheetAppState.normalizeSheets(resp),
				lastRefresh: Date.now(),
				error: null,
			});
		} catch (err: unknown) {
			// User-initiated (toolbar Refresh): surface the failure so the user
			// knows the displayed values are stale, and rethrow so the page shows a
			// toast. Background poll: stay silent (a transient blip shouldn't spam),
			// but keep one diagnostic line for debugging a persistent failure.
			if (action.userInitiated) {
				ctx.patchState({ error: WorksheetAppState.errorMessage(err) });
				throw err;
			}
			console.warn('[WorksheetAppState] RefreshWorksheet (poll) failed', err);
		}
	}

	@Action(SetWorksheetPolling)
	setPolling(ctx: StateContext<WorksheetAppStateModel>, action: SetWorksheetPolling): void {
		ctx.patchState({ polling: action.polling });
	}

	/** Flatten the load response into ordered sheets (matching {@code sheetNames}). */
	private static normalizeSheets(resp: WorksheetLoadResponse): ReadonlyArray<WorksheetSheet> {
		const names: ReadonlyArray<string> = resp?.sheetNames ?? [];
		return names.filter((name: string): boolean => !!resp.sheetData?.[name]).map((name: string): WorksheetSheet => ({ name, ...resp.sheetData[name] }));
	}

	private static errorMessage(err: unknown): string {
		if (err instanceof HttpErrorResponse) {
			return err.error?.error ?? err.message ?? 'Failed to load worksheet';
		}
		return 'Failed to load worksheet';
	}
}
